/**
 * The gates a set is recorded for, in order (CS r4a added `exit-2a`, CS r5a `exit-2b`, CS r5c
 * `exit-2c`).
 */
export const GATE_PACKAGE_GATES = [
    'editor-alpha',
    'exit-1a',
    'exit-1b',
    'exit-2a',
    'exit-2b',
    'exit-2c',
];
export const EDITOR_ALPHA_PACKAGES = {
    '1.0a': ['1', '1b', '1c'],
    '1.0b': 'all',
    '1.0c': 'all',
    '1.1': ['s1', 's2', 's3', 's4'],
    '1.A': ['1', '1b'],
    '1.B-1': ['1'],
    '1.D': ['1'],
    '1.R': ['s1', 's2'],
    '1.E-1': ['s1', 's2', 's3'],
    '1.E-2': ['s1', 's2', 's3', 's3b'],
    '1.F-1': ['s1', 's2', 's3', 's4'],
    '1.F-2': ['s1', 's2'],
    '1.G': ['s1a', 's1b', 's1c', 's2'],
    '1.H': ['s1', 's1b', 's1c', 's2', 'tier2'],
    '1.X': ['alpha'],
    '1.Q': ['alpha'],
    CS: ['r1', 'r1b'],
    TS: ['r1', 'r1b', 'r2', 'r2b'],
    UI: ['r1'],
};
export const EXIT_1A_PACKAGES = {
    // Stage 3, the server save round (DECISIONS 168, 237 ruling 3).
    '1.A': ['2', '3'],
    '1.B-1': ['1b'],
    '1.B-2': 'all',
    '1.C': ['1'],
    // Stage 3: the search performance round (DECISIONS 168, 237 ruling 3).
    '1.D': ['1b', '3'],
    // Decision 237 ruling 3: the keystroke round, with CS's consent.
    '1.E-1': ['s4'],
    '1.E-2': ['s4'],
    '1.F-1': ['s5', 's6'],
    '1.S': 'all',
    '1.GS': 'all',
    '1.I-1': 'all',
    // Stage s3, the sync and save round (DECISIONS 168, 237 ruling 3), with CS's consent.
    '1.G': ['s3'],
    '1.I-2': ['s1', 's2'],
    '1.I-3': 'all',
    '1.J': ['1', '2'],
    // Stage s3, the solver performance round (DECISIONS 168, 237 ruling 3), with CS's consent.
    '1.K-2': ['s3'],
    '1.V-1': 'all',
    '1.V-2': 'all',
    '1.W': ['1'],
    '1.P': 'all',
    '1.X': ['1a'],
    // Stage perf, the representative fixtures and the session perf spec (DECISIONS 308).
    '1.Q': ['1a', 'perf'],
    // UI-first hardening (DECISIONS 231, 234).
    '1.UF': ['s1', 's2'],
    OPS: ['1'],
};
/**
 * What exit 1b adds by stage. Every other Phase 1 package that owns work (not a split parent) is
 * added whole, except 1.F-s, which joins at exit 2a (DECISIONS 532): exit 1b holds every other
 * Phase 1 stage. 1.E-2 and OPS also have Phase 2a stages (1.E-2's s5, OPS's 3), so exit 1b pins
 * their Phase 1 stages instead of `all` (CS r4a; DECISIONS 177 item f); OPS's stage 2 runs
 * srd:seed after postRestore (1.B-1 and 1.0a ruling 11).
 */
export const EXIT_1B_STAGED_PACKAGES = {
    // The contract steward's exit-1b round (CS r3 deliverable 75; open point 29, DECISIONS 214);
    // r3d, the demo's NPC Faction field (CS r3d deliverable 100; DECISIONS 371, 373).
    CS: ['r3', 'r3a', 'r3b', 'r3c', 'r3d'],
    '1.0a': ['2', '2b'],
    '1.A': ['2'],
    // Stage 2 split into 2 and 2b (1.B-1 open point 1; CS r2b, DECISIONS 133).
    '1.B-1': ['2', '2b'],
    '1.D': ['2'],
    '1.E-2': ['s1', 's2', 's3', 's3b', 's4'],
    '1.Q': ['1b'],
    OPS: ['1', '2'],
};
/**
 * What exit 2a adds (CS r4a deliverable 111; 2a plan §5): the contract steward's r4a, r4b and r4c,
 * the test steward's r5a and r5b, the UI steward's r3 (its r3b, the Lightbox, gates nothing), OPS
 * stage 3, and the 2a packages whole. 1.E-2's stage s5 does not gate (DECISIONS 176 item 6).
 * 1.F-s, the Phase 1 package the GM moved after exit 1b, joins here by its stage s1 (DECISIONS 532,
 * 539; narrowed from `all` by CS r5c under DECISIONS 609, its performance stage s2 joining at
 * exit 2c).
 */
export const EXIT_2A_PACKAGES = {
    // CS r4c (deliverable 131): the editor's sheet lister and the single-page import shapes.
    CS: ['r4a', 'r4b', 'r4c'],
    TS: ['r5a', 'r5b'],
    UI: ['r3'],
    OPS: ['3'],
    '2.A': 'all',
    '2.B-1': 'all',
    '2.B-2': 'all',
    '2.K': 'all',
    '2.Q': 'all',
    // DECISIONS 532 and 539: 1.F-s (the slash picker's SRD group) ships right after exit 1b; s1 only
    // since DECISIONS 609 (CS r5c), s2 being the performance round's.
    '1.F-s': ['s1'],
};
/**
 * What exit 2b adds (CS r5a deliverable 150; 2b plan §5, DECISIONS 212 item 7): the contract
 * steward's r5a and r5b, the test steward's Phase 2b stages, the UI steward's r4, 1.M-1's stage s3
 * and 1.N-3's s4, the 2b packages whole, and 2.Q's journeys of stages s3 and s4. The test
 * steward's r6a and r6b are not marked in `ts.md` on the integration branch at this round
 * (brief/ts-r6 has not merged), so TS is listed `all` and a later CS round narrows it to
 * `['r6a', 'r6b']` once the markers land. 1.M-1 and 1.N-3 are already whole from exit 1b, and
 * 2.Q from exit 2a, so the cumulative set keeps them `all` (DECISIONS 219).
 */
export const EXIT_2B_PACKAGES = {
    CS: ['r5a', 'r5b'],
    TS: ['r6a', 'r6b', 'r6c'],
    UI: ['r4'],
    '1.M-1': ['s3'],
    '1.N-3': ['s4'],
    '2.C': 'all',
    '2.D': 'all',
    '2.E': 'all',
    '2.F': 'all',
    '2.J': 'all',
    '2.L': 'all',
    '2.Q': ['s3', 's4'],
};
/**
 * What exit 2c adds, as CS r5c seeds it (deliverable 174; DECISIONS 607, 609's gate-set ruling):
 * the Phase 2b performance round, presented at exit 2c, the waivers of DECISIONS 548 standing
 * until then: CS r5c (its schema), 1.N-3 s5, 1.D stages 4 and 5, 1.A stage 4, 1.Q perf2, 1.E-1 s6,
 * 1.F-s s2 and 1.K-2 s4. 1.N-3, 1.E-1 and 1.K-2 are whole from exit 1b, so the cumulative set
 * keeps them `all`; 1.A, 1.D and 1.Q, staged from exit 1b, gain these stages. CS r6a extends the
 * set with 2.I's route stages, and CS r6c with Phase 2c (deliverable 206).
 */
export const EXIT_2C_PACKAGES = {
    '1.N-3': ['s5'],
    '1.D': ['4', '5'],
    '1.A': ['4'],
    '1.E-1': ['s6'],
    '1.F-s': ['s2'],
    '1.K-2': ['s4'],
    // CS r6c deliverable 206 (2c plan §5; DECISIONS 222 ruling 23, 764 addendum item 5): Phase 2c
    // itself. The contract steward's r6a, r6c and r6b; 1.Q's 1b; the 2c packages whole (2.I whole
    // since r6c: r6a listed its three route stages); the UI steward's r5a and r5b, OPS stage 5 and
    // 2.Q's journeys of stage s5, each marked in its brief on feature. The test steward's r7a and r7b
    // are marked in ts.md on feature (TS r7a merged), so TS is listed by those two rounds (r5a 150's
    // form).
    // CS r5c (its schema) and 1.Q perf2 are the performance round's, as r5c seeded them.
    CS: ['r5c', 'r6a', 'r6c', 'r6b'],
    '1.Q': ['perf2', '1b'],
    '2.G': 'all',
    '2.I': 'all',
    '2.I-2': 'all',
    '2.H': 'all',
    '2.M': 'all',
    TS: ['r7a', 'r7b'],
    UI: ['r5a', 'r5b'],
    OPS: ['5'],
    '2.Q': ['s5'],
};
/** Ids that split into others and own no row (1.0b deliverable 15). */
export const SPLIT_PARENT_PACKAGES = [
    '1.0',
    '1.B',
    '1.E',
    '1.F',
    '1.I',
    '1.K',
    '1.L',
    '1.M',
    '1.N',
    '1.O',
    '1.V',
    '1.Z',
    '1.AB',
    '1.AC',
];
//# sourceMappingURL=rows-gate-packages.js.map